import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faTimes, faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../AxiosInstance';
import { SIGNAL_CONFIG } from '../../nodes/scada/port_config';
import { ELEMENT_SIM_DEFAULTS } from '../../nodes/scada/simulation_engine';
import styles from './scada.module.css';

const NO_BINDING_TYPES = ['shape_rect', 'shape_circle', 'shape_line', 'text_label', 'image_embed'];

/**
 * Single-signal variable picker (device → variable, filtered by dataType).
 */
function SignalPicker({ label, dataType, bound, devices, onChange }) {
    const { t } = useTranslation();
    const [selectedDeviceId, setSelectedDeviceId] = useState('');
    const [variables, setVariables] = useState([]);

    useEffect(() => {
        const devId = bound?.device_id;
        setSelectedDeviceId(devId ? String(devId) : '');
        if (devId) loadVariables(devId);
        else setVariables([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bound?.variable_data_id]);

    const loadVariables = (deviceId) => {
        if (!deviceId) { setVariables([]); return; }
        axiosInstance.get(`variable_data/?device_id=${deviceId}`).then(res => {
            const all = res.data.results || res.data;
            const filtered = dataType
                ? all.filter(v => (v.variable?.data_type ?? 'number') === dataType)
                : all;
            setVariables(filtered);
        });
    };

    const handleDeviceChange = (deviceId) => {
        setSelectedDeviceId(deviceId);
        setVariables([]);
        onChange({ variable_data_id: null, variable_data_detail: null, device_id: null, device_name: '' });
        loadVariables(deviceId);
    };

    const handleVariableChange = (vid) => {
        const vdId = vid ? parseInt(vid) : null;
        const vd = variables.find(v => v.id === vdId);
        const device = devices.find(d => String(d.id) === selectedDeviceId);
        onChange({
            variable_data_id: vdId,
            variable_data_detail: vd ? {
                id: vd.id,
                variable_id: vd.variable?.id ?? vd.variable_id,
                variable_name: vd.variable?.name ?? '',
                unit: vd.variable?.unit ?? '',
                device_id: parseInt(selectedDeviceId),
                device_name: device?.name ?? '',
            } : null,
            device_id: parseInt(selectedDeviceId) || null,
            device_name: device?.name ?? '',
        });
    };

    return (
        <div style={{ marginBottom: 10 }}>
            <div className={styles.inspectorLabel} style={{ color: '#818CF8', marginBottom: 2 }}>{label}</div>
            <select className={styles.inspectorSelect} value={selectedDeviceId} onChange={e => handleDeviceChange(e.target.value)}>
                <option value="">{t('scada.selectDevice')}</option>
                {devices.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            {selectedDeviceId && (
                <select className={styles.inspectorSelect} value={bound?.variable_data_id || ''} onChange={e => handleVariableChange(e.target.value)}>
                    <option value="">{t('scada.noVariable')}</option>
                    {variables.map(v => (
                        <option key={v.id} value={v.id}>
                            {v.variable?.name ?? v.id}
                            {v.variable?.unit ? ` (${v.variable.unit})` : ''}
                        </option>
                    ))}
                </select>
            )}
            {bound?.variable_data_id && (
                <div style={{ fontSize: 10, color: '#4F46E5', marginTop: 2 }}>
                    ✓ {bound.device_name} — {bound.variable_data_detail?.variable_name}
                </div>
            )}
        </div>
    );
}

/**
 * Button picker locked to a specific device (for actuator open/close buttons).
 */
function ActuatorButtonPicker({ label, deviceId, bound, onChangeConfig, configKey, styles: s }) {
    const { t } = useTranslation();
    const [variables, setVariables] = useState([]);

    useEffect(() => {
        if (!deviceId) { setVariables([]); return; }
        axiosInstance.get(`variable_data/?device_id=${deviceId}`).then(res => {
            setVariables(res.data.results || res.data);
        });
    }, [deviceId]);

    const handleChange = (vid) => {
        const vdId = vid ? parseInt(vid) : null;
        const vd = variables.find(v => v.id === vdId);
        onChangeConfig(configKey, vd ? {
            variable_data_id: vd.id,
            variable_id: vd.variable?.id ?? vd.variable_id,
            variable_name: vd.variable?.name ?? '',
        } : null);
    };

    if (!deviceId) {
        return (
            <div style={{ marginBottom: 8 }}>
                <div className={s.inspectorLabel} style={{ color: '#818CF8' }}>{label}</div>
                <div style={{ fontSize: 11, color: '#64748B' }}>{t('scada.actuatorNoDevice')}</div>
            </div>
        );
    }

    return (
        <div style={{ marginBottom: 8 }}>
            <div className={s.inspectorLabel} style={{ color: '#818CF8' }}>{label}</div>
            <select className={s.inspectorSelect} value={bound?.variable_data_id || ''} onChange={e => handleChange(e.target.value)}>
                <option value="">{t('scada.noVariable')}</option>
                {variables.map(v => (
                    <option key={v.id} value={v.id}>
                        {v.variable?.name ?? v.id}
                        {v.variable?.unit ? ` (${v.variable.unit})` : ''}
                    </option>
                ))}
            </select>
            {bound?.variable_name && (
                <div style={{ fontSize: 10, color: '#4F46E5', marginTop: 2 }}>✓ {bound.variable_name}</div>
            )}
        </div>
    );
}

/**
 * Multi-variable picker for alarm_banner.
 */
function AlarmVariablePicker({ config, devices, onChange }) {
    const { t } = useTranslation();
    const [selectedDeviceId, setSelectedDeviceId] = useState('');
    const [variables, setVariables] = useState([]);
    const alarmVars = config.alarm_variables || [];
    const alarmDetails = config.alarm_variable_details || {};

    const loadVariables = (deviceId) => {
        if (!deviceId) { setVariables([]); return; }
        axiosInstance.get(`variable_data/?device_id=${deviceId}`).then(res => {
            setVariables(res.data.results || res.data);
        });
    };

    const addVariable = (vid) => {
        if (!vid) return;
        const vdId = parseInt(vid);
        if (alarmVars.includes(vdId)) return;
        const vd = variables.find(v => v.id === vdId);
        const device = devices.find(d => String(d.id) === selectedDeviceId);
        onChange({
            alarm_variables: [...alarmVars, vdId],
            alarm_variable_details: {
                ...alarmDetails,
                [vdId]: {
                    variable_data_id: vdId,
                    device_id: parseInt(selectedDeviceId),
                    variable_name: vd?.variable?.name ?? '',
                    device_name: device?.name ?? '',
                    medium_threshold: vd?.medium_threshold ?? null,
                    high_threshold: vd?.high_threshold ?? null,
                },
            },
        });
    };

    const removeVariable = (vdId) => {
        const newDetails = { ...alarmDetails };
        delete newDetails[vdId];
        onChange({
            alarm_variables: alarmVars.filter(id => id !== vdId),
            alarm_variable_details: newDetails,
        });
    };

    return (
        <div>
            <div className={styles.inspectorLabel}>{t('scada.alarmVariables')}</div>
            <select className={styles.inspectorSelect} value={selectedDeviceId}
                onChange={e => { setSelectedDeviceId(e.target.value); loadVariables(e.target.value); }}>
                <option value="">{t('scada.selectDevice')}</option>
                {devices.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            {selectedDeviceId && (
                <select className={styles.inspectorSelect} value="" onChange={e => addVariable(e.target.value)}>
                    <option value="">{t('scada.addVariable')}</option>
                    {variables.filter(v => !alarmVars.includes(v.id)).map(v => (
                        <option key={v.id} value={v.id}>{v.variable?.name ?? v.id}</option>
                    ))}
                </select>
            )}
            {alarmVars.map(vdId => (
                <div key={vdId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11, color: '#F1F5F9', background: '#1E293B', borderRadius: 4, padding: '3px 8px', marginBottom: 3, marginTop: 4 }}>
                    <span>{alarmDetails[vdId]?.device_name} — {alarmDetails[vdId]?.variable_name}</span>
                    <button onClick={() => removeVariable(vdId)} style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', padding: '0 2px' }}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
            ))}
        </div>
    );
}

const WATCHABLE_TYPES = new Set(Object.keys(SIGNAL_CONFIG).filter(t =>
    SIGNAL_CONFIG[t]?.some(s => s.key === 'state' || s.key === 'level')
));

/**
 * Dropdown that lists signals emitted by sim_recipe_steps across all nodes.
 * Falls back to a text input when the current value isn't in the list or when
 * the user picks "Custom…".
 */
function SignalSelect({ value, allNodes, onChange }) {
    const { t } = useTranslation();
    const [customMode, setCustomMode] = useState(false);

    const signals = [...new Set(
        allNodes.flatMap(n => (n.data?.config?.sim?.sim_recipe_steps || [])
            .map(s => s.signal).filter(Boolean))
    )].sort();

    const valueInList = signals.includes(value);
    const showInput = customMode || (!!value && !valueInList);

    return (
        <div>
            <select className={styles.inspectorSelect}
                value={showInput ? '__custom' : (value ?? '')}
                onChange={e => {
                    if (e.target.value === '__custom') {
                        setCustomMode(true);
                        onChange(undefined);
                    } else {
                        setCustomMode(false);
                        onChange(e.target.value || undefined);
                    }
                }}>
                <option value="">— {t('scada.none')} —</option>
                {signals.map(sig => <option key={sig} value={sig}>{sig}</option>)}
                <option value="__custom">✏ {t('scada.simCustomSignal')}</option>
            </select>
            {showInput && (
                <input className={styles.inspectorInput} style={{ marginTop: 4, marginBottom: 0 }}
                    autoFocus placeholder="signal_name"
                    value={value ?? ''}
                    onChange={e => onChange(e.target.value || undefined)} />
            )}
            {signals.length === 0 && !showInput && (
                <div style={{ fontSize: 9, color: '#64748B', marginTop: 2 }}>{t('scada.simNoSignalsHint')}</div>
            )}
        </div>
    );
}

/**
 * Dropdown that lists exclusive group names already used across all nodes.
 * "New group…" reveals a text input to create a new name.
 */
function GroupSelect({ value, allNodes, onChange }) {
    const { t } = useTranslation();
    const [newMode, setNewMode] = useState(false);

    const groups = [...new Set(
        allNodes.map(n => n.data?.config?.sim?.sim_exclusive_valve_group).filter(Boolean)
    )].sort();

    const valueInList = groups.includes(value);
    const showInput = newMode || (!!value && !valueInList);

    return (
        <div>
            <select className={styles.inspectorSelect}
                value={showInput ? '__new' : (value ?? '')}
                onChange={e => {
                    if (e.target.value === '__new') {
                        setNewMode(true);
                        onChange(undefined);
                    } else {
                        setNewMode(false);
                        onChange(e.target.value || undefined);
                    }
                }}>
                <option value="">— {t('scada.none')} —</option>
                {groups.map(g => <option key={g} value={g}>{g}</option>)}
                <option value="__new">➕ {t('scada.simNewGroup')}</option>
            </select>
            {showInput && (
                <input className={styles.inspectorInput} style={{ marginTop: 4, marginBottom: 0 }}
                    autoFocus placeholder="group_name"
                    value={value ?? ''}
                    onChange={e => onChange(e.target.value || undefined)} />
            )}
        </div>
    );
}

function SimulationConfigSection({ node, allNodes, config, onChange }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const signals = SIGNAL_CONFIG[node.type];
    if (!signals || signals.length === 0) return null;

    const simCfg = config.sim || {};
    const defaults = ELEMENT_SIM_DEFAULTS[node.type] || ELEMENT_SIM_DEFAULTS._default;
    const triggers = simCfg.sim_triggers || [];

    const setSim = (key, val) => onChange(node.id, {
        config: { ...config, sim: { ...simCfg, [key]: val } },
    });

    const addTrigger = () => setSim('sim_triggers', [
        ...triggers,
        { watch_node_id: '', condition: 'state_on', action: 'start' },
    ]);

    const updateTrigger = (idx, partial) => {
        const tr = [...triggers];
        tr[idx] = { ...tr[idx], ...partial };
        setSim('sim_triggers', tr);
    };

    const removeTrigger = (idx) => setSim('sim_triggers', triggers.filter((_, i) => i !== idx));

    const watchableNodes = allNodes.filter(n => n.id !== node.id && WATCHABLE_TYPES.has(n.type));

    /** Returns condition options appropriate for the watched node type. */
    const getConditionOptions = (watchNodeId) => {
        const watched = allNodes.find(n => n.id === watchNodeId);
        const watchedType = watched?.type || '';
        const opts = [
            { value: 'state_on',      label: t('scada.condStateOn') },
            { value: 'state_off',     label: t('scada.condStateOff') },
            { value: 'state_rising',  label: t('scada.condStateRising') },
            { value: 'state_falling', label: t('scada.condStateFalling') },
        ];
        if (watchedType === 'ribbon_blender') {
            opts.push({ value: 'gate_1_open',    label: t('scada.condGate1Open') });
            opts.push({ value: 'gate_1_closed',  label: t('scada.condGate1Closed') });
            opts.push({ value: 'gate_1_rising',  label: t('scada.condGate1Rising') });
            opts.push({ value: 'gate_1_falling', label: t('scada.condGate1Falling') });
            const gateCount = parseInt(watched?.data?.config?.gate_count ?? 0);
            if (gateCount >= 2) {
                opts.push({ value: 'gate_2_open',    label: t('scada.condGate2Open') });
                opts.push({ value: 'gate_2_closed',  label: t('scada.condGate2Closed') });
                opts.push({ value: 'gate_2_rising',  label: t('scada.condGate2Rising') });
                opts.push({ value: 'gate_2_falling', label: t('scada.condGate2Falling') });
            }
        } else if (watchedType === 'cooler_blender') {
            opts.push({ value: 'gate_open',      label: t('scada.condGateOpen') });
            opts.push({ value: 'gate_closed',    label: t('scada.condGateClosed') });
            opts.push({ value: 'gate_rising',    label: t('scada.condGateRising') });
            opts.push({ value: 'gate_falling',   label: t('scada.condGateFalling') });
            opts.push({ value: 'level_above_80', label: t('scada.condLevelAbove80') });
            opts.push({ value: 'level_below_20', label: t('scada.condLevelBelow20') });
        } else if (['tank', 'silo', 'sensor_level'].includes(watchedType)) {
            opts.push({ value: 'level_above_80', label: t('scada.condLevelAbove80') });
            opts.push({ value: 'level_below_20', label: t('scada.condLevelBelow20') });
        }
        return opts;
    };

    return (
        <div className={styles.inspectorSection}>
            <button
                onClick={() => setOpen(v => !v)}
                style={{ background: 'none', border: 'none', color: '#F59E0B', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, padding: 0 }}
            >
                <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} style={{ fontSize: 10 }} />
                {t('scada.simConfig')}
            </button>

            {open && (
                <div style={{ marginTop: 10 }}>
                    {/* Bucket elevator godet physics */}
                    {node.type === 'bucket_elevator' && (
                        <div style={{ marginBottom: 8 }}>
                            <div style={{ fontSize: 10, color: '#F59E0B', fontWeight: 600, marginBottom: 6 }}>
                                {t('scada.simGodets')}
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 6 }}>
                                <div>
                                    <div className={styles.inspectorLabel}>{t('scada.simGodetCapacity')}</div>
                                    <input className={styles.inspectorInput} style={{ marginBottom: 0 }} type="number"
                                        placeholder="3" min={0.1} step={0.1}
                                        value={simCfg.sim_godet_capacity_kg ?? ''}
                                        onChange={e => setSim('sim_godet_capacity_kg',
                                            e.target.value === '' ? undefined : parseFloat(e.target.value))} />
                                    <div style={{ fontSize: 9, color: '#64748B', marginTop: 2 }}>kg</div>
                                </div>
                                <div>
                                    <div className={styles.inspectorLabel}>{t('scada.simGodetCount')}</div>
                                    <input className={styles.inspectorInput} style={{ marginBottom: 0 }} type="number"
                                        placeholder="360" min={1}
                                        value={simCfg.sim_godet_count ?? ''}
                                        onChange={e => setSim('sim_godet_count',
                                            e.target.value === '' ? undefined : parseInt(e.target.value))} />
                                </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 4 }}>
                                <div>
                                    <div className={styles.inspectorLabel}>{t('scada.simRevolutionMin')}</div>
                                    <input className={styles.inspectorInput} style={{ marginBottom: 0 }} type="number"
                                        placeholder="12" min={0.1} step={0.1}
                                        value={simCfg.sim_revolution_min ?? ''}
                                        onChange={e => setSim('sim_revolution_min',
                                            e.target.value === '' ? undefined : parseFloat(e.target.value))} />
                                    <div style={{ fontSize: 9, color: '#64748B', marginTop: 2 }}>min</div>
                                </div>
                                <div>
                                    <div className={styles.inspectorLabel}>{t('scada.simReferenceHz')}</div>
                                    <input className={styles.inspectorInput} style={{ marginBottom: 0 }} type="number"
                                        placeholder="40" min={1}
                                        value={simCfg.sim_reference_hz ?? ''}
                                        onChange={e => setSim('sim_reference_hz',
                                            e.target.value === '' ? undefined : parseFloat(e.target.value))} />
                                    <div style={{ fontSize: 9, color: '#64748B', marginTop: 2 }}>Hz</div>
                                </div>
                            </div>
                            {/* Computed rate display */}
                            {(() => {
                                const gc = parseFloat(simCfg.sim_godet_count ?? 360);
                                const gk = parseFloat(simCfg.sim_godet_capacity_kg ?? 3);
                                const rm = parseFloat(simCfg.sim_revolution_min ?? 12);
                                const rate = (gc * gk / rm).toFixed(1);
                                return (
                                    <div style={{ fontSize: 9, color: '#94A3B8', marginTop: 4 }}>
                                        {t('scada.simTransportRate')}: <strong style={{ color: '#F59E0B' }}>{rate} kg/min</strong>
                                    </div>
                                );
                            })()}
                            {/* Discharge order */}
                            <div style={{ marginTop: 8 }}>
                                <div className={styles.inspectorLabel}>{t('scada.simDischargeOrder')}</div>
                                <select className={styles.inspectorSelect}
                                    value={simCfg.sim_discharge_order ?? 'fifo'}
                                    onChange={e => setSim('sim_discharge_order', e.target.value)}>
                                    <option value="fifo">{t('scada.simFIFO')}</option>
                                    <option value="lifo">{t('scada.simLIFO')}</option>
                                    <option value="depleted">{t('scada.simDepleted')}</option>
                                </select>
                                <div style={{ fontSize: 9, color: '#64748B', marginTop: 2 }}>
                                    {simCfg.sim_discharge_order === 'lifo'
                                        ? t('scada.simLIFOHint')
                                        : simCfg.sim_discharge_order === 'depleted'
                                        ? t('scada.simDepletedHint')
                                        : t('scada.simFIFOHint')}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Container capacity (tank, silo, hopper_screw) + capacitive sensor thresholds */}
                    {['tank', 'silo', 'hopper_screw'].includes(node.type) && (
                        <div style={{ marginBottom: 8 }}>
                            <div className={styles.inspectorLabel}>{t('scada.simCapacityKg')}</div>
                            <input className={styles.inspectorInput} style={{ marginBottom: 4 }} type="number"
                                placeholder="1000" min={1}
                                value={simCfg.sim_capacity_kg ?? ''}
                                onChange={e => setSim('sim_capacity_kg',
                                    e.target.value === '' ? undefined : parseFloat(e.target.value))} />
                            {/* Capacitive sensor thresholds — shown when level_cap_count > 0 */}
                            {parseInt(node.data?.config?.level_cap_count ?? 0) > 0 && (
                                <div style={{ marginTop: 4 }}>
                                    <div className={styles.inspectorLabel}>{t('scada.simCapLevels')}</div>
                                    {Array.from({ length: parseInt(node.data.config.level_cap_count) }, (_, i) => {
                                        const defaultPct = Math.round((i + 1) / parseInt(node.data.config.level_cap_count) * 100);
                                        return (
                                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                                <span style={{ fontSize: 10, color: '#94A3B8', width: 52, flexShrink: 0 }}>
                                                    {t('scada.simSensor')} {i + 1}:
                                                </span>
                                                <input className={styles.inspectorInput} style={{ marginBottom: 0, flex: 1 }}
                                                    type="number" min={0} max={100}
                                                    placeholder={String(defaultPct)}
                                                    value={simCfg.sim_cap_levels?.[i] ?? ''}
                                                    onChange={e => {
                                                        const capCount = parseInt(node.data.config.level_cap_count);
                                                        const arr = Array.from({ length: capCount }, (_, j) =>
                                                            simCfg.sim_cap_levels?.[j] ?? undefined
                                                        );
                                                        arr[i] = e.target.value === '' ? undefined : parseFloat(e.target.value);
                                                        setSim('sim_cap_levels', arr);
                                                    }} />
                                                <span style={{ fontSize: 10, color: '#64748B' }}>%</span>
                                            </div>
                                        );
                                    })}
                                    {/* Requesting product trigger sensors */}
                                    {['hopper_screw', 'tank', 'silo', 'reactor', 'screw_conveyor'].includes(node.type) && (
                                        <div style={{ marginTop: 8, borderTop: '1px solid #1E293B', paddingTop: 6 }}>
                                            <div className={styles.inspectorLabel}>{t('scada.simRequestTriggers')}</div>
                                            {[
                                                { labelKey: 'simRequestOn',  sensorKey: 'sim_request_on_sensor',  edgeKey: 'sim_request_on_edge',  defaultIdx: 0,                                                           defaultEdge: 'falling' },
                                                { labelKey: 'simRequestOff', sensorKey: 'sim_request_off_sensor', edgeKey: 'sim_request_off_edge', defaultIdx: parseInt(node.data.config.level_cap_count) - 2, defaultEdge: 'rising'  },
                                            ].map(({ labelKey, sensorKey, edgeKey, defaultIdx, defaultEdge }) => (
                                                <div key={labelKey} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                                    <span style={{ fontSize: 10, color: '#94A3B8', width: 52, flexShrink: 0 }}>
                                                        {t(`scada.${labelKey}`)}:
                                                    </span>
                                                    <input className={styles.inspectorInput}
                                                        style={{ marginBottom: 0, width: 64, flexShrink: 0 }}
                                                        type="number" min={0} max={parseInt(node.data.config.level_cap_count) - 1}
                                                        placeholder={String(defaultIdx)}
                                                        value={simCfg[sensorKey] ?? ''}
                                                        onChange={e => setSim(sensorKey, e.target.value === '' ? undefined : parseInt(e.target.value))} />
                                                    <select className={styles.inspectorInput}
                                                        style={{ marginBottom: 0, flex: 1, padding: '1px 4px' }}
                                                        value={simCfg[edgeKey] ?? defaultEdge}
                                                        onChange={e => setSim(edgeKey, e.target.value)}>
                                                        <option value="falling">↓ {t('scada.simEdgeFalling')}</option>
                                                        <option value="rising">↑ {t('scada.simEdgeRising')}</option>
                                                    </select>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Hopper screw: screw feed rate */}
                    {node.type === 'hopper_screw' && (
                        <div style={{ marginBottom: 8 }}>
                            <div className={styles.inspectorLabel}>{t('scada.simScrewRate')}</div>
                            <input className={styles.inspectorInput} style={{ marginBottom: 0 }} type="number"
                                placeholder="22.79" min={0.1} step={0.01}
                                value={simCfg.sim_screw_rate_kg_min ?? ''}
                                onChange={e => setSim('sim_screw_rate_kg_min',
                                    e.target.value === '' ? undefined : parseFloat(e.target.value))} />
                            <div style={{ fontSize: 9, color: '#64748B', marginTop: 2 }}>{t('scada.simScrewRateHint')}</div>
                        </div>
                    )}

                    {/* Packager: internal buffer capacity + request threshold */}
                    {['packager_single', 'packager_multi', 'packager_doypack'].includes(node.type) && (
                        <div style={{ marginBottom: 8 }}>
                            <div className={styles.inspectorLabel}>{t('scada.simPackagerBuffer')}</div>
                            <input className={styles.inspectorInput} style={{ marginBottom: 0 }} type="number"
                                placeholder="10" min={1}
                                value={simCfg.sim_packager_hopper_kg ?? ''}
                                onChange={e => setSim('sim_packager_hopper_kg',
                                    e.target.value === '' ? undefined : parseFloat(e.target.value))} />
                            <div style={{ fontSize: 9, color: '#64748B', marginTop: 2 }}>{t('scada.simPackagerBufferHint')}</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 6 }}>
                                <div>
                                    <div className={styles.inspectorLabel}>{t('scada.simRequestOnPct')}</div>
                                    <input className={styles.inspectorInput} style={{ marginBottom: 0 }} type="number"
                                        placeholder="30" min={0} max={100}
                                        value={simCfg.sim_request_on_pct ?? ''}
                                        onChange={e => setSim('sim_request_on_pct',
                                            e.target.value === '' ? undefined : parseFloat(e.target.value))} />
                                </div>
                                <div>
                                    <div className={styles.inspectorLabel}>{t('scada.simRequestOffPct')}</div>
                                    <input className={styles.inspectorInput} style={{ marginBottom: 0 }} type="number"
                                        placeholder="90" min={0} max={100}
                                        value={simCfg.sim_request_off_pct ?? ''}
                                        onChange={e => setSim('sim_request_off_pct',
                                            e.target.value === '' ? undefined : parseFloat(e.target.value))} />
                                </div>
                            </div>
                            <div style={{ fontSize: 9, color: '#64748B', marginTop: 2 }}>{t('scada.simRequestThresholdHint')}</div>
                        </div>
                    )}

                    {/* Initial level for vessel-type elements */}
                    {['tank', 'silo', 'hopper_screw', 'reactor', 'v_blender'].includes(node.type) && (
                        <div style={{ marginBottom: 8 }}>
                            <div className={styles.inspectorLabel}>{t('scada.simInitialLevel')}</div>
                            <input className={styles.inspectorInput} style={{ marginBottom: 6 }} type="number"
                                min={0} max={100} placeholder="80"
                                value={simCfg.sim_initial_level ?? ''}
                                onChange={e => setSim('sim_initial_level',
                                    e.target.value === '' ? undefined : parseFloat(e.target.value))} />
                            {/* Drain-only toggle (tank/silo) */}
                            {['tank', 'silo'].includes(node.type) && (
                                <label style={{ display: 'flex', alignItems: 'center', gap: 6,
                                    fontSize: 11, color: '#94A3B8', cursor: 'pointer', marginBottom: 0 }}>
                                    <input type="checkbox"
                                        checked={simCfg.sim_tank_refill !== false}
                                        onChange={e => setSim('sim_tank_refill', e.target.checked ? undefined : false)} />
                                    {t('scada.simTankRefill')}
                                </label>
                            )}
                        </div>
                    )}

                    {/* Chiller target temperature */}
                    {node.type === 'chiller' && (
                        <div style={{ marginBottom: 8 }}>
                            <div className={styles.inspectorLabel}>{t('scada.simChillerTarget')}</div>
                            <input className={styles.inspectorInput} style={{ marginBottom: 0 }} type="number"
                                min={-10} max={20} placeholder="6"
                                value={simCfg.sim_chiller_target ?? ''}
                                onChange={e => setSim('sim_chiller_target',
                                    e.target.value === '' ? undefined : parseFloat(e.target.value))} />
                            <div style={{ fontSize: 9, color: '#64748B', marginTop: 2 }}>{t('scada.simChillerTargetHint')}</div>
                        </div>
                    )}

                    {/* Always On & Startup Offset — machines with a state signal */}
                    {signals?.some(s => s.key === 'state') && (
                        <div style={{ marginBottom: 8 }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6,
                                fontSize: 11, color: '#94A3B8', cursor: 'pointer', marginBottom: 4 }}>
                                <input type="checkbox"
                                    checked={!!simCfg.sim_always_on}
                                    onChange={e => setSim('sim_always_on', e.target.checked || undefined)} />
                                {t('scada.simAlwaysOn')}
                            </label>
                            {!simCfg.sim_always_on && (
                                <div>
                                    <div className={styles.inspectorLabel}>{t('scada.simStartupOffset')}</div>
                                    <input className={styles.inspectorInput} style={{ marginBottom: 0 }} type="number"
                                        placeholder="0" min={0} step={60}
                                        value={simCfg.sim_startup_offset_s ?? ''}
                                        onChange={e => setSim('sim_startup_offset_s',
                                            e.target.value === '' ? undefined : parseFloat(e.target.value))} />
                                    <div style={{ fontSize: 9, color: '#64748B', marginTop: 2 }}>{t('scada.simStartupOffsetHint')}</div>
                                </div>
                            )}
                        </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 8 }}>
                        <div>
                            <div className={styles.inspectorLabel}>{t('scada.simTimeOn')}</div>
                            <input className={styles.inspectorInput} style={{ marginBottom: 0 }} type="number"
                                placeholder={String(defaults.sim_cycle_on)}
                                value={simCfg.sim_cycle_on ?? ''}
                                onChange={e => setSim('sim_cycle_on', e.target.value === '' ? undefined : parseInt(e.target.value))} />
                        </div>
                        <div>
                            <div className={styles.inspectorLabel}>{t('scada.simTimeOff')}</div>
                            <input className={styles.inspectorInput} style={{ marginBottom: 0 }} type="number"
                                placeholder={String(defaults.sim_cycle_off)}
                                value={simCfg.sim_cycle_off ?? ''}
                                onChange={e => setSim('sim_cycle_off', e.target.value === '' ? undefined : parseInt(e.target.value))} />
                        </div>
                    </div>

                    {/* Cooler blender target temp */}
                    {node.type === 'cooler_blender' && (
                        <div style={{ marginBottom: 8 }}>
                            <div className={styles.inspectorLabel}>{t('scada.simCoolerTarget')}</div>
                            <input className={styles.inspectorInput} style={{ marginBottom: 0 }} type="number"
                                min={0} max={30} placeholder="8"
                                value={simCfg.sim_cooler_target ?? ''}
                                onChange={e => setSim('sim_cooler_target',
                                    e.target.value === '' ? undefined : parseFloat(e.target.value))} />
                            <div style={{ fontSize: 9, color: '#64748B', marginTop: 2 }}>{t('scada.simCoolerTargetHint')}</div>
                        </div>
                    )}

                    {/* Packager production config */}
                    {['packager_single', 'packager_multi', 'packager_doypack'].includes(node.type) && (
                        <div style={{ marginBottom: 8 }}>
                            <div style={{ fontSize: 10, color: '#F59E0B', fontWeight: 600, marginBottom: 6 }}>
                                {t('scada.simProduction')}
                            </div>
                            <div style={{ marginBottom: 6 }}>
                                <div className={styles.inspectorLabel}>{t('scada.simFillTime')}</div>
                                <input className={styles.inspectorInput} style={{ marginBottom: 0 }} type="number"
                                    placeholder="6" min={0.5} step={0.5}
                                    value={simCfg.sim_fill_time ?? ''}
                                    onChange={e => setSim('sim_fill_time',
                                        e.target.value === '' ? undefined : parseFloat(e.target.value))} />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                                <div>
                                    <div className={styles.inspectorLabel}>{t('scada.simNominalSpeed')}</div>
                                    <input className={styles.inspectorInput} style={{ marginBottom: 0 }} type="number"
                                        placeholder="0" min={0}
                                        value={simCfg.sim_nominal_speed ?? ''}
                                        onChange={e => setSim('sim_nominal_speed',
                                            e.target.value === '' ? undefined : parseFloat(e.target.value))} />
                                    <div style={{ fontSize: 9, color: '#64748B', marginTop: 2 }}>{t('scada.simSpeedUnit')}</div>
                                </div>
                                <div>
                                    <div className={styles.inspectorLabel}>{t('scada.simUnitWeight')}</div>
                                    <input className={styles.inspectorInput} style={{ marginBottom: 0 }} type="number"
                                        placeholder="0" min={0}
                                        value={simCfg.sim_unit_weight_g ?? ''}
                                        onChange={e => setSim('sim_unit_weight_g',
                                            e.target.value === '' ? undefined : parseFloat(e.target.value))} />
                                    <div style={{ fontSize: 9, color: '#64748B', marginTop: 2 }}>{t('scada.simWeightUnit')}</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Ribbon blender — process config + recipe steps */}
                    {node.type === 'ribbon_blender' && (
                        <div style={{ marginBottom: 8 }}>
                            <div style={{ fontSize: 10, color: '#F59E0B', fontWeight: 600, marginBottom: 6 }}>
                                {t('scada.simProcessConfig')}
                            </div>
                            <div style={{ marginBottom: 6 }}>
                                <div className={styles.inspectorLabel}>{t('scada.simBatchCapacity')}</div>
                                <input className={styles.inspectorInput} style={{ marginBottom: 0 }} type="number"
                                    placeholder="600" min={1}
                                    value={simCfg.sim_batch_capacity_kg ?? ''}
                                    onChange={e => setSim('sim_batch_capacity_kg',
                                        e.target.value === '' ? undefined : parseFloat(e.target.value))} />
                                <div style={{ fontSize: 9, color: '#64748B', marginTop: 2 }}>kg</div>
                            </div>
                            <div style={{ marginBottom: 6 }}>
                                <div className={styles.inspectorLabel}>{t('scada.simDischargeDuration')}</div>
                                <input className={styles.inspectorInput} style={{ marginBottom: 0 }} type="number"
                                    placeholder="360" min={1}
                                    value={simCfg.sim_discharge_duration_s ?? ''}
                                    onChange={e => setSim('sim_discharge_duration_s',
                                        e.target.value === '' ? undefined : parseFloat(e.target.value))} />
                                <div style={{ fontSize: 9, color: '#64748B', marginTop: 2 }}>{t('scada.simDischargeDurationHint')}</div>
                            </div>
                            <div style={{ marginBottom: 6 }}>
                                <div className={styles.inspectorLabel}>{t('scada.simOutletTemp')}</div>
                                <input className={styles.inspectorInput} style={{ marginBottom: 0 }} type="number"
                                    placeholder="55" min={0} step={1}
                                    value={simCfg.sim_outlet_temp ?? ''}
                                    onChange={e => setSim('sim_outlet_temp',
                                        e.target.value === '' ? undefined : parseFloat(e.target.value))} />
                                <div style={{ fontSize: 9, color: '#64748B', marginTop: 2 }}>{t('scada.simOutletTempHint')}</div>
                            </div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6,
                                fontSize: 11, color: '#94A3B8', cursor: 'pointer', marginBottom: 8 }}>
                                <input type="checkbox"
                                    checked={!!simCfg.sim_check_downstream_capacity_before_gate}
                                    onChange={e => setSim('sim_check_downstream_capacity_before_gate', e.target.checked || undefined)} />
                                {t('scada.simCheckDownstreamCapacity')}
                            </label>
                            {/* Recipe steps editor */}
                            <div style={{ fontSize: 10, color: '#F59E0B', fontWeight: 600, marginBottom: 4 }}>
                                {t('scada.simRecipeSteps')}
                            </div>
                            {(simCfg.sim_recipe_steps || []).map((step, i) => {
                                const steps = simCfg.sim_recipe_steps || [];
                                const updateStep = (partial) => {
                                    const updated = [...steps];
                                    updated[i] = { ...updated[i], ...partial };
                                    setSim('sim_recipe_steps', updated);
                                };
                                return (
                                    <div key={i} style={{ background: '#0F172A', borderRadius: 6, padding: 8, marginBottom: 6 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                            <span style={{ fontSize: 10, color: '#94A3B8' }}>{t('scada.simStep')} {i + 1}</span>
                                            <button onClick={() => setSim('sim_recipe_steps', steps.filter((_, j) => j !== i))}
                                                style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', padding: 0 }}>
                                                <FontAwesomeIcon icon={faTimes} style={{ fontSize: 10 }} />
                                            </button>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginBottom: 4 }}>
                                            <div>
                                                <div className={styles.inspectorLabel}>{t('scada.simStepName')}</div>
                                                <input className={styles.inspectorInput} style={{ marginBottom: 0 }}
                                                    placeholder="step_name"
                                                    value={step.name ?? ''}
                                                    onChange={e => updateStep({ name: e.target.value })} />
                                            </div>
                                            <div>
                                                <div className={styles.inspectorLabel}>{t('scada.simStepDuration')}</div>
                                                <input className={styles.inspectorInput} style={{ marginBottom: 0 }} type="number"
                                                    placeholder="300" min={1}
                                                    value={step.duration_s ?? ''}
                                                    onChange={e => updateStep({ duration_s: e.target.value === '' ? undefined : parseInt(e.target.value) })} />
                                            </div>
                                        </div>
                                        <div className={styles.inspectorLabel}>{t('scada.simStepSignal')}</div>
                                        <input className={styles.inspectorInput} style={{ marginBottom: 0 }}
                                            placeholder="signal_name"
                                            value={step.signal ?? ''}
                                            onChange={e => updateStep({ signal: e.target.value || null })} />
                                    </div>
                                );
                            })}
                            <button
                                onClick={() => setSim('sim_recipe_steps', [
                                    ...(simCfg.sim_recipe_steps || []),
                                    { name: '', duration_s: 300, signal: null },
                                ])}
                                style={{ background: 'none', border: '1px dashed #334155', borderRadius: 6,
                                    color: '#64748B', cursor: 'pointer', fontSize: 11, padding: '4px 10px',
                                    width: '100%', marginBottom: 4 }}>
                                {t('scada.simAddStep')}
                            </button>
                            {(simCfg.sim_recipe_steps?.length > 0) && (() => {
                                const total = (simCfg.sim_recipe_steps || []).reduce((s, st) => s + (st.duration_s || 0), 0);
                                const cycleOn = simCfg.sim_cycle_on || 1800;
                                return (
                                    <div style={{ fontSize: 9, color: total === cycleOn ? '#22C55E' : '#F59E0B', marginTop: 2 }}>
                                        {t('scada.simRecipeTotal')}: {total}s / {cycleOn}s
                                    </div>
                                );
                            })()}
                        </div>
                    )}

                    {/* Cooler blender — hold time + backpressure + discharge duration */}
                    {node.type === 'cooler_blender' && (
                        <div style={{ marginBottom: 8 }}>
                            <div className={styles.inspectorLabel}>{t('scada.simHoldTime')}</div>
                            <input className={styles.inspectorInput} style={{ marginBottom: 0 }} type="number"
                                placeholder="1800" min={0}
                                value={simCfg.sim_hold_time_s ?? ''}
                                onChange={e => setSim('sim_hold_time_s',
                                    e.target.value === '' ? undefined : parseFloat(e.target.value))} />
                            <div style={{ fontSize: 9, color: '#64748B', marginTop: 2, marginBottom: 8 }}>{t('scada.simHoldTimeHint')}</div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6,
                                fontSize: 11, color: '#94A3B8', cursor: 'pointer', marginBottom: 8 }}>
                                <input type="checkbox"
                                    checked={!!simCfg.sim_block_gate_if_downstream_at_capacity}
                                    onChange={e => setSim('sim_block_gate_if_downstream_at_capacity', e.target.checked || undefined)} />
                                {t('scada.simBlockGateDownstream')}
                            </label>
                            <div className={styles.inspectorLabel}>{t('scada.simDischargeDuration')}</div>
                            <input className={styles.inspectorInput} style={{ marginBottom: 0 }} type="number"
                                placeholder="720" min={1}
                                value={simCfg.sim_discharge_duration_s ?? ''}
                                onChange={e => setSim('sim_discharge_duration_s',
                                    e.target.value === '' ? undefined : parseFloat(e.target.value))} />
                            <div style={{ fontSize: 9, color: '#64748B', marginTop: 2 }}>{t('scada.simDischargeDurationHint')}</div>
                        </div>
                    )}

                    {/* Pump — demand-driven */}
                    {node.type === 'pump' && (
                        <div style={{ marginBottom: 8 }}>
                            <div style={{ fontSize: 10, color: '#F59E0B', fontWeight: 600, marginBottom: 6 }}>
                                {t('scada.simDemandDriven')}
                            </div>
                            <div className={styles.inspectorLabel}>{t('scada.simRunOnSignal')}</div>
                            <SignalSelect
                                value={simCfg.sim_run_on_downstream_signal}
                                allNodes={allNodes}
                                onChange={v => setSim('sim_run_on_downstream_signal', v)} />
                            <div className={styles.inspectorLabel} style={{ marginTop: 6 }}>{t('scada.simExclusiveGroup')}</div>
                            <GroupSelect
                                value={simCfg.sim_exclusive_valve_group}
                                allNodes={allNodes}
                                onChange={v => setSim('sim_exclusive_valve_group', v)} />
                            <div style={{ fontSize: 9, color: '#64748B', marginTop: 2 }}>{t('scada.simExclusiveGroupHint')}</div>
                        </div>
                    )}

                    {/* Valves — demand-driven */}
                    {['valve_gate', 'valve_ball', 'valve_butterfly', 'valve_3way'].includes(node.type) && (
                        <div style={{ marginBottom: 8 }}>
                            <div style={{ fontSize: 10, color: '#F59E0B', fontWeight: 600, marginBottom: 6 }}>
                                {t('scada.simDemandDriven')}
                            </div>
                            <div className={styles.inspectorLabel}>{t('scada.simOpenOnSignal')}</div>
                            <SignalSelect
                                value={simCfg.sim_open_on_downstream_signal}
                                allNodes={allNodes}
                                onChange={v => setSim('sim_open_on_downstream_signal', v)} />
                            <div className={styles.inspectorLabel} style={{ marginTop: 6 }}>{t('scada.simExclusiveGroup')}</div>
                            <GroupSelect
                                value={simCfg.sim_exclusive_valve_group}
                                allNodes={allNodes}
                                onChange={v => setSim('sim_exclusive_valve_group', v)} />
                            <div style={{ fontSize: 9, color: '#64748B', marginTop: 2 }}>{t('scada.simExclusiveGroupHint')}</div>
                        </div>
                    )}

                    {/* Tank / silo — estimated consumption + emit at capacity */}
                    {['tank', 'silo'].includes(node.type) && (
                        <div style={{ marginBottom: 8 }}>
                            <div style={{ fontSize: 10, color: '#F59E0B', fontWeight: 600, marginBottom: 6 }}>
                                {t('scada.simConsumption')}
                            </div>
                            <div className={styles.inspectorLabel}>{t('scada.simConsumptionMode')}</div>
                            <select className={styles.inspectorSelect} style={{ marginBottom: 6 }}
                                value={simCfg.sim_consumption_mode ?? 'normal'}
                                onChange={e => setSim('sim_consumption_mode', e.target.value === 'normal' ? undefined : e.target.value)}>
                                <option value="normal">{t('scada.simConsumptionNormal')}</option>
                                <option value="estimated">{t('scada.simConsumptionEstimated')}</option>
                            </select>
                            {simCfg.sim_consumption_mode === 'estimated' && (
                                <>
                                    <div className={styles.inspectorLabel}>{t('scada.simInitialLevelKg')}</div>
                                    <input className={styles.inspectorInput} style={{ marginBottom: 4 }} type="number"
                                        placeholder="1000" min={0}
                                        value={simCfg.sim_initial_level_kg ?? ''}
                                        onChange={e => setSim('sim_initial_level_kg',
                                            e.target.value === '' ? undefined : parseFloat(e.target.value))} />
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 4 }}>
                                        <div>
                                            <div className={styles.inspectorLabel}>{t('scada.simConsumptionPerBatch')}</div>
                                            <input className={styles.inspectorInput} style={{ marginBottom: 0 }} type="number"
                                                placeholder="8" min={0} step={0.5}
                                                value={simCfg.sim_consumption_per_batch_kg ?? ''}
                                                onChange={e => setSim('sim_consumption_per_batch_kg',
                                                    e.target.value === '' ? undefined : parseFloat(e.target.value))} />
                                        </div>
                                        <div>
                                            <div className={styles.inspectorLabel}>{t('scada.simConsumptionTrigger')}</div>
                                            <SignalSelect
                                                value={simCfg.sim_consumption_trigger_signal}
                                                allNodes={allNodes}
                                                onChange={v => setSim('sim_consumption_trigger_signal', v)} />
                                        </div>
                                    </div>
                                </>
                            )}
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6,
                                fontSize: 11, color: '#94A3B8', cursor: 'pointer', marginTop: 4 }}>
                                <input type="checkbox"
                                    checked={!!simCfg.sim_emit_at_capacity}
                                    onChange={e => setSim('sim_emit_at_capacity', e.target.checked || undefined)} />
                                {t('scada.simEmitAtCapacity')}
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6,
                                fontSize: 11, color: '#94A3B8', cursor: 'pointer', marginTop: 4 }}>
                                <input type="checkbox"
                                    checked={!!simCfg.sim_emit_at_empty}
                                    onChange={e => setSim('sim_emit_at_empty', e.target.checked || undefined)} />
                                {t('scada.simEmitAtEmpty')}
                            </label>
                        </div>
                    )}

                    {/* Hopper screw — emit at capacity / at empty */}
                    {node.type === 'hopper_screw' && (
                        <div style={{ marginBottom: 8 }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6,
                                fontSize: 11, color: '#94A3B8', cursor: 'pointer', marginBottom: 4 }}>
                                <input type="checkbox"
                                    checked={!!simCfg.sim_emit_at_capacity}
                                    onChange={e => setSim('sim_emit_at_capacity', e.target.checked || undefined)} />
                                {t('scada.simEmitAtCapacity')}
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6,
                                fontSize: 11, color: '#94A3B8', cursor: 'pointer' }}>
                                <input type="checkbox"
                                    checked={!!simCfg.sim_emit_at_empty}
                                    onChange={e => setSim('sim_emit_at_empty', e.target.checked || undefined)} />
                                {t('scada.simEmitAtEmpty')}
                            </label>
                        </div>
                    )}

                    {/* Packager — consumption rate (lanes come from config.lanes) */}
                    {['packager_single', 'packager_multi', 'packager_doypack'].includes(node.type) && (
                        <div style={{ marginBottom: 8 }}>
                            {(() => {
                                const lines = parseFloat(config.lanes ?? 1);
                                const weightG = parseFloat(simCfg.sim_unit_weight_g ?? 0);
                                const gpm = parseFloat(simCfg.sim_nominal_speed ?? 0);
                                if (weightG > 0 && gpm > 0) {
                                    const rate = (lines * weightG / 1000 * gpm).toFixed(2);
                                    return (
                                        <div style={{ fontSize: 9, color: '#94A3B8', marginBottom: 4 }}>
                                            {t('scada.simConsumptionRate')}: <strong style={{ color: '#F59E0B' }}>{rate} kg/min</strong>
                                            <div style={{ color: '#64748B', marginTop: 1 }}>{t('scada.simConsumptionRateHint')}</div>
                                        </div>
                                    );
                                }
                                return null;
                            })()}
                            <div className={styles.inspectorLabel}>{t('scada.simConsumptionRateOverride')}</div>
                            <input className={styles.inspectorInput} style={{ marginBottom: 0 }} type="number"
                                placeholder={t('scada.simAutoComputed')} min={0} step={0.01}
                                value={simCfg.sim_consumption_rate_kg_min ?? ''}
                                onChange={e => setSim('sim_consumption_rate_kg_min',
                                    e.target.value === '' ? undefined : parseFloat(e.target.value))} />
                            <div style={{ fontSize: 9, color: '#64748B', marginTop: 2 }}>{t('scada.simConsumptionRateOverrideHint')}</div>
                        </div>
                    )}

                    {/* Box former / Box sealer: throughput */}
                    {['box_former', 'box_sealer'].includes(node.type) && (
                        <div style={{ marginBottom: 8 }}>
                            <div className={styles.inspectorLabel}>{t('scada.simThroughputCartMin', 'Velocidad (cartones/min)')}</div>
                            <input className={styles.inspectorInput} style={{ marginBottom: 0 }} type="number"
                                placeholder="30" min={1}
                                value={simCfg.sim_throughput_cart_min ?? ''}
                                onChange={e => setSim('sim_throughput_cart_min',
                                    e.target.value === '' ? undefined : parseFloat(e.target.value))} />
                            <div style={{ fontSize: 9, color: '#64748B', marginTop: 2 }}>
                                {t('scada.simThroughputHint', 'Cartones formados o sellados por minuto')}
                            </div>
                        </div>
                    )}

                    <div className={styles.inspectorLabel} style={{ marginBottom: 4 }}>{t('scada.simTriggers')}</div>
                    {triggers.map((tr, idx) => (
                        <div key={idx} style={{ background: '#0F172A', borderRadius: 6, padding: 8, marginBottom: 6 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                <span style={{ fontSize: 10, color: '#94A3B8' }}>{t('scada.simTriggerN', { n: idx + 1 })}</span>
                                <button onClick={() => removeTrigger(idx)}
                                    style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', padding: 0 }}>
                                    <FontAwesomeIcon icon={faTimes} style={{ fontSize: 10 }} />
                                </button>
                            </div>
                            <div className={styles.inspectorLabel}>{t('scada.when')}</div>
                            <select className={styles.inspectorSelect} value={tr.watch_node_id}
                                onChange={e => updateTrigger(idx, { watch_node_id: e.target.value, condition: 'state_on' })}>
                                <option value="">{t('scada.selectEquipment')}</option>
                                {watchableNodes.map(n => (
                                    <option key={n.id} value={n.id}>
                                        {n.data?.label || `${n.type} (${n.id})`}
                                    </option>
                                ))}
                            </select>
                            {tr.watch_node_id && (() => {
                                const opts = getConditionOptions(tr.watch_node_id);
                                const validValue = opts.some(o => o.value === tr.condition) ? tr.condition : opts[0]?.value;
                                return (
                                    <select className={styles.inspectorSelect} style={{ marginTop: 4 }} value={validValue}
                                        onChange={e => updateTrigger(idx, { condition: e.target.value })}>
                                        {opts.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                );
                            })()}
                            <div className={styles.inspectorLabel} style={{ marginTop: 4 }}>{t('scada.triggerAction')}</div>
                            <select className={styles.inspectorSelect} value={tr.action}
                                onChange={e => updateTrigger(idx, { action: e.target.value })}>
                                <option value="start">{t('scada.actionStart')}</option>
                                <option value="stop">{t('scada.actionStop')}</option>
                            </select>
                        </div>
                    ))}
                    <button onClick={addTrigger}
                        style={{ background: 'none', border: '1px dashed #334155', borderRadius: 6,
                            color: '#64748B', cursor: 'pointer', fontSize: 11, padding: '4px 10px',
                            width: '100%', marginTop: 2 }}>
                        {t('scada.addTrigger')}
                    </button>
                </div>
            )}
        </div>
    );
}

/**
 * Inspector panel (right sidebar).
 */
export default function SCADAElementInspector({ node, projectId, allNodes = [], onChange, onDelete }) {
    const { t } = useTranslation();
    const [devices, setDevices] = useState([]);

    useEffect(() => {
        if (!projectId) return;
        axiosInstance.get(`device/?project_id=${projectId}`).then(res => {
            setDevices(res.data.results || res.data);
        });
    }, [projectId]);

    if (!node) {
        return (
            <div className={styles.inspector}>
                <div className={styles.inspectorHeader}>
                    <div className={styles.inspectorTitle}>{t('scada.palette')}</div>
                </div>
                <div style={{ padding: '16px 14px', color: '#475569', fontSize: 12 }}>
                    {t('scada.selectElement')}
                </div>
            </div>
        );
    }

    const data = node.data || {};
    const config = data.config || {};
    const signals = SIGNAL_CONFIG[node.type] ?? null;

    const set = (key, val) => onChange(node.id, { [key]: val });
    const setConfig = (key, val) => onChange(node.id, { config: { ...config, [key]: val } });

    const setSignalBinding = (signalKey, bindingData) => {
        const variables = { ...(config.variables || {}) };
        variables[signalKey] = bindingData;
        onChange(node.id, { config: { ...config, variables } });
    };

    const inletLabel  = (n) => `${n} ${n > 1 ? t('scada.inletPlural') : t('scada.inletSingular')}`;
    const outletLabel = (n) => `${n} ${n > 1 ? t('scada.outletPlural') : t('scada.outletSingular')}`;
    const gateLabel   = (n) => `${n} ${n > 1 ? t('scada.gatePlural') : t('scada.gateSingular')}`;
    const sensorLabel = (n) => `${n} ${n > 1 ? t('scada.sensorPluralN') : t('scada.sensorSingularN')}`;

    return (
        <div className={styles.inspector}>
            <div className={styles.inspectorHeader}>
                <div className={styles.inspectorTitle}>{node.type}</div>
            </div>

            {/* Label */}
            <div className={styles.inspectorSection}>
                <div className={styles.inspectorLabel}>{t('scada.elementLabel')}</div>
                <input className={styles.inspectorInput} value={data.label || ''} onChange={e => set('label', e.target.value)} placeholder={t('scada.elementLabel')} />
                <label style={{ display: 'flex', alignItems: 'center', gap: 6,
                    fontSize: 11, color: '#94A3B8', cursor: 'pointer', marginTop: 6 }}>
                    <input type="checkbox"
                        checked={!!data.config?.show_label}
                        onChange={e => setConfig('show_label', e.target.checked || undefined)} />
                    {t('scada.showLabel')}
                </label>
            </div>

            {/* Size */}
            <div className={styles.inspectorSection}>
                <div className={styles.inspectorLabel}>{t('scada.elementWidth')} / {t('scada.elementHeight')}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                    <input className={styles.inspectorInput} style={{ marginBottom: 0 }} type="number" value={data.width || 80} onChange={e => set('width', parseInt(e.target.value) || 80)} />
                    <input className={styles.inspectorInput} style={{ marginBottom: 0 }} type="number" value={data.height || 80} onChange={e => set('height', parseInt(e.target.value) || 80)} />
                </div>
            </div>

            {/* Multi-signal bindings filtered by dataType */}
            {signals !== null && !NO_BINDING_TYPES.includes(node.type) && signals.length > 0 && (
                <div className={styles.inspectorSection}>
                    <div className={styles.inspectorLabel} style={{ marginBottom: 6 }}>{t('scada.bindVariable')}</div>
                    {signals.map(sig => (
                        <SignalPicker
                            key={sig.key}
                            label={t(sig.label)}
                            dataType={sig.dataType}
                            bound={config.variables?.[sig.key]}
                            devices={devices}
                            onChange={binding => setSignalBinding(sig.key, binding)}
                        />
                    ))}
                </div>
            )}

            {/* alarm_banner: multi-variable */}
            {node.type === 'alarm_banner' && (
                <div className={styles.inspectorSection}>
                    <AlarmVariablePicker
                        config={config}
                        devices={devices}
                        onChange={partial => onChange(node.id, { config: { ...config, ...partial } })}
                    />
                </div>
            )}

            {/* custom_machine */}
            {node.type === 'custom_machine' && data.machine_type_detail && (
                <div className={styles.inspectorSection}>
                    <div className={styles.inspectorLabel}>{t('scada.machineType')}</div>
                    <div style={{ fontSize: 12, color: '#F1F5F9', fontWeight: 600, marginBottom: 4 }}>{data.machine_type_detail.name}</div>
                    <div style={{ fontSize: 10, color: '#64748B', marginBottom: 8 }}>
                        {data.machine_type_detail.states_config?.length || 0} {t('scada.states').toLowerCase()} · {data.machine_type_detail.actions_config?.length || 0} {t('scada.actions').toLowerCase()}
                    </div>
                    <SignalPicker label={t('scada.stateVariable')} dataType={null} bound={config.variables?.state} devices={devices} onChange={b => setSignalBinding('state', b)} />
                </div>
            )}

            {/* Type-specific config */}
            {['tank', 'sensor_level'].includes(node.type) && (
                <div className={styles.inspectorSection}>
                    <div className={styles.inspectorLabel}>{t('scada.minMax')}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                        <input className={styles.inspectorInput} style={{ marginBottom: 0 }} type="number" placeholder={t('scada.minValue')} value={config.min_value ?? 0} onChange={e => setConfig('min_value', parseFloat(e.target.value))} />
                        <input className={styles.inspectorInput} style={{ marginBottom: 0 }} type="number" placeholder={t('scada.maxValue')} value={config.max_value ?? 100} onChange={e => setConfig('max_value', parseFloat(e.target.value))} />
                    </div>
                    <div style={{ marginTop: 6 }}>
                        <div className={styles.inspectorLabel}>{t('scada.fillColor')}</div>
                        <input type="color" value={config.fill_color || '#3B82F6'} onChange={e => setConfig('fill_color', e.target.value)} className={styles.colorSwatch} />
                    </div>
                </div>
            )}

            {['sensor_temp', 'sensor_pressure', 'sensor_flow', 'sensor_humidity', 'display_digital'].includes(node.type) && (
                <div className={styles.inspectorSection}>
                    <div className={styles.inspectorLabel}>{t('scada.decimalPlaces')}</div>
                    <input className={styles.inspectorInput} type="number" value={config.decimals ?? 1} onChange={e => setConfig('decimals', parseInt(e.target.value))} min={0} max={4} />
                </div>
            )}

            {['valve_gate', 'valve_ball', 'valve_butterfly', 'valve_check', 'valve_3way', 'led_indicator'].includes(node.type) && (
                <div className={styles.inspectorSection}>
                    <div className={styles.inspectorLabel}>{t('scada.thresholdOn')}</div>
                    <input className={styles.inspectorInput} type="number" value={config.threshold ?? 0.5} onChange={e => setConfig('threshold', parseFloat(e.target.value))} step="0.1" />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                        <div>
                            <div className={styles.inspectorLabel}>{t('scada.onColor')}</div>
                            <input type="color" value={config.on_color || '#22C55E'} onChange={e => setConfig('on_color', e.target.value)} className={styles.colorSwatch} />
                        </div>
                        <div>
                            <div className={styles.inspectorLabel}>{t('scada.offColor')}</div>
                            <input type="color" value={config.off_color || '#EF4444'} onChange={e => setConfig('off_color', e.target.value)} className={styles.colorSwatch} />
                        </div>
                    </div>
                    {['valve_gate', 'valve_ball', 'valve_butterfly', 'valve_3way', 'led_indicator'].includes(node.type) && (
                        <>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 12, color: '#94A3B8', cursor: 'pointer' }}>
                                <input type="checkbox" checked={!!config.is_actuator} onChange={e => setConfig('is_actuator', e.target.checked)} />
                                {t('scada.isActuator')}
                            </label>
                            {!!config.is_actuator && (() => {
                                // Device for action buttons — explicit override or defaults to feedback device
                                const actuatorDeviceId = config.actuator_device_id
                                    ?? node.data?.variable_data_detail?.device_id
                                    ?? null;

                                const isGate = ['valve_gate', 'valve_butterfly', 'led_indicator'].includes(node.type);
                                const is3way = node.type === 'valve_3way';
                                const isBall = node.type === 'valve_ball';

                                const missingGate = isGate && actuatorDeviceId && (!config.actuator_open?.variable_id || !config.actuator_close?.variable_id);
                                const missing3way = is3way && actuatorDeviceId && (!config.actuator_pos_a?.variable_id || !config.actuator_pos_b?.variable_id);
                                const missingBall = isBall && actuatorDeviceId && !config.actuator_slider?.variable_id;

                                return (
                                    <div style={{ marginTop: 8 }}>
                                        {/* Action device picker (independent of feedback) */}
                                        <div className={styles.inspectorLabel}>{t('scada.actuatorDevice')}</div>
                                        <select className={styles.inspectorSelect}
                                            value={actuatorDeviceId || ''}
                                            onChange={e => setConfig('actuator_device_id', parseInt(e.target.value) || null)}
                                        >
                                            <option value="">{t('scada.selectDevice')}</option>
                                            {devices.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                        </select>

                                        {/* valve_gate / valve_butterfly / led_indicator: open + close buttons */}
                                        {isGate && (<>
                                            <ActuatorButtonPicker label={t('scada.actuatorOpenBtn')} deviceId={actuatorDeviceId}
                                                bound={config.actuator_open} onChangeConfig={setConfig} configKey="actuator_open" styles={styles} />
                                            <ActuatorButtonPicker label={t('scada.actuatorCloseBtn')} deviceId={actuatorDeviceId}
                                                bound={config.actuator_close} onChangeConfig={setConfig} configKey="actuator_close" styles={styles} />
                                            {missingGate && <div style={{ fontSize: 11, color: '#F59E0B', marginBottom: 8, lineHeight: 1.4 }}>{t('scada.actuatorBtnsWarning')}</div>}
                                        </>)}

                                        {/* valve_3way: 3 position buttons */}
                                        {is3way && (<>
                                            <ActuatorButtonPicker label={t('scada.actuatorPosA')} deviceId={actuatorDeviceId}
                                                bound={config.actuator_pos_a} onChangeConfig={setConfig} configKey="actuator_pos_a" styles={styles} />
                                            <ActuatorButtonPicker label={t('scada.actuatorPosB')} deviceId={actuatorDeviceId}
                                                bound={config.actuator_pos_b} onChangeConfig={setConfig} configKey="actuator_pos_b" styles={styles} />
                                            <ActuatorButtonPicker label={t('scada.actuatorPosC')} deviceId={actuatorDeviceId}
                                                bound={config.actuator_pos_c} onChangeConfig={setConfig} configKey="actuator_pos_c" styles={styles} />
                                            {missing3way && <div style={{ fontSize: 11, color: '#F59E0B', marginBottom: 8, lineHeight: 1.4 }}>{t('scada.actuatorPosBtnsWarning')}</div>}
                                        </>)}

                                        {/* valve_ball: single analog slider variable */}
                                        {isBall && (<>
                                            <ActuatorButtonPicker label={t('scada.actuatorSliderVar')} deviceId={actuatorDeviceId}
                                                bound={config.actuator_slider} onChangeConfig={setConfig} configKey="actuator_slider" styles={styles} />
                                            {missingBall && <div style={{ fontSize: 11, color: '#F59E0B', marginBottom: 8, lineHeight: 1.4 }}>{t('scada.actuatorSliderWarning')}</div>}
                                        </>)}

                                        <div className={styles.inspectorLabel}>{t('scada.lockAfterSend')}</div>
                                        <input className={styles.inspectorInput} type="number" min={1} max={120}
                                            placeholder="5"
                                            value={config.toggle_lock_duration ?? ''}
                                            onChange={e => setConfig('toggle_lock_duration',
                                                e.target.value === '' ? undefined : parseInt(e.target.value))} />
                                        {!isBall && (
                                            <div style={{ fontSize: 10, color: '#64748B', marginTop: 2 }}>
                                                {t('scada.earlyUnlock')}
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                        </>
                    )}
                </div>
            )}

            {node.type === 'regulator' && (
                <div className={styles.inspectorSection}>
                    <div className={styles.inspectorLabel}>{t('scada.barColor')}</div>
                    <input type="color" value={config.bar_color || '#4F46E5'} onChange={e => setConfig('bar_color', e.target.value)} className={styles.colorSwatch} />
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 12, color: '#94A3B8', cursor: 'pointer' }}>
                        <input type="checkbox" checked={!!config.is_actuator} onChange={e => setConfig('is_actuator', e.target.checked)} />
                        {t('scada.isActuatorCtrl')}
                    </label>
                    {!!config.is_actuator && (
                        <div style={{ marginTop: 8 }}>
                            <div className={styles.inspectorLabel}>{t('scada.lockAfterSend')}</div>
                            <input className={styles.inspectorInput} type="number" min={1} max={120}
                                placeholder="5"
                                value={config.toggle_lock_duration ?? ''}
                                onChange={e => setConfig('toggle_lock_duration',
                                    e.target.value === '' ? undefined : parseInt(e.target.value))} />
                        </div>
                    )}
                </div>
            )}

            {node.type === 'text_label' && (
                <div className={styles.inspectorSection}>
                    <div className={styles.inspectorLabel}>{t('scada.fontSize')}</div>
                    <input className={styles.inspectorInput} type="number" value={config.font_size || 14} onChange={e => setConfig('font_size', parseInt(e.target.value))} />
                    <div className={styles.inspectorLabel}>{t('scada.onColor')}</div>
                    <input type="color" value={config.color || '#F1F5F9'} onChange={e => setConfig('color', e.target.value)} className={styles.colorSwatch} />
                </div>
            )}

            {node.type === 'image_embed' && (
                <div className={styles.inspectorSection}>
                    <div className={styles.inspectorLabel}>{t('scada.imageSource', 'Imagen')}</div>
                    <input className={styles.inspectorInput} type="text"
                        placeholder={t('scada.imageUrlPlaceholder', 'URL de la imagen...')}
                        value={config.src || ''}
                        onChange={e => setConfig('src', e.target.value)} />
                    <div style={{ marginTop: 6, marginBottom: 6 }}>
                        <label style={{ display: 'inline-block', padding: '4px 10px', fontSize: 11,
                            background: '#334155', color: '#F1F5F9', borderRadius: 4, cursor: 'pointer',
                            border: '1px solid #475569' }}>
                            {t('scada.uploadImage', 'Subir imagen')}
                            <input type="file" accept="image/*" style={{ display: 'none' }}
                                onChange={e => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    const fd = new FormData();
                                    fd.append('image', file);
                                    axiosInstance.post('scada_element/upload_image/', fd, {
                                        headers: { 'Content-Type': 'multipart/form-data' },
                                    }).then(res => {
                                        if (res.data?.url) setConfig('src', res.data.url);
                                    }).catch(err => console.error('Image upload failed:', err));
                                }} />
                        </label>
                    </div>
                    {config.src && (
                        <div style={{ marginBottom: 6 }}>
                            <img src={config.src} alt="preview"
                                style={{ maxWidth: '100%', maxHeight: 80, objectFit: 'contain',
                                    borderRadius: 4, border: '1px solid #334155' }} />
                        </div>
                    )}
                    <div className={styles.inspectorLabel} style={{ marginTop: 4 }}>{t('scada.objectFit', 'Ajuste')}</div>
                    <select className={styles.inspectorSelect}
                        value={config.object_fit || 'contain'}
                        onChange={e => setConfig('object_fit', e.target.value)}>
                        <option value="contain">Contain</option>
                        <option value="cover">Cover</option>
                        <option value="fill">Fill</option>
                        <option value="none">None</option>
                    </select>
                    <div className={styles.inspectorLabel} style={{ marginTop: 6 }}>{t('scada.borderRadius', 'Bordes redondeados')}</div>
                    <input className={styles.inspectorInput} type="number" min={0}
                        value={config.border_radius || 0}
                        onChange={e => setConfig('border_radius', parseInt(e.target.value))} />
                </div>
            )}

            {['shape_rect', 'shape_circle'].includes(node.type) && (
                <div className={styles.inspectorSection}>
                    <div className={styles.inspectorLabel}>{t('scada.fill')}</div>
                    <input type="color" value={config.fill || '#334155'} onChange={e => setConfig('fill', e.target.value)} className={styles.colorSwatch} />
                    <div className={styles.inspectorLabel} style={{ marginTop: 6 }}>{t('scada.fillOpacity')} ({Math.round((config.fill_opacity ?? 0.5) * 100)}%)</div>
                    <input type="range" min={0} max={1} step={0.05}
                        value={config.fill_opacity ?? 0.5}
                        onChange={e => setConfig('fill_opacity', parseFloat(e.target.value))}
                        style={{ width: '100%' }} />
                    <div className={styles.inspectorLabel} style={{ marginTop: 6 }}>{t('scada.borderColor')}</div>
                    <input type="color" value={config.border_color || '#475569'} onChange={e => setConfig('border_color', e.target.value)} className={styles.colorSwatch} />
                </div>
            )}

            {node.type === 'packager_multi' && (
                <div className={styles.inspectorSection}>
                    <div className={styles.inspectorLabel}>{t('scada.lanes')}</div>
                    <input className={styles.inspectorInput} type="number" value={config.lanes || 4} onChange={e => setConfig('lanes', parseInt(e.target.value))} min={2} max={20} />
                </div>
            )}

            {/* Strip Folder config: ristras, bolsitas, peso, lanes */}
            {node.type === 'strip_folder' && (
                <div className={styles.inspectorSection}>
                    <div className={styles.inspectorLabel} style={{ fontWeight: 700, marginBottom: 6 }}>{t('scada.stripFolderConfig', 'Dobladora de Ristras')}</div>
                    <div style={{ marginBottom: 6 }}>
                        <div className={styles.inspectorLabel}>{t('scada.ristrasPerCarton', 'Ristras por carton')}</div>
                        <input className={styles.inspectorInput} type="number"
                            value={config.ristras_per_carton ?? 6} min={1} max={20}
                            onChange={e => setConfig('ristras_per_carton', parseInt(e.target.value))} />
                    </div>
                    <div style={{ marginBottom: 6 }}>
                        <div className={styles.inspectorLabel}>{t('scada.bolsitasPerRistra', 'Bolsitas por ristra')}</div>
                        <input className={styles.inspectorInput} type="number"
                            value={config.bolsitas_per_ristra ?? 5} min={1} max={20}
                            onChange={e => setConfig('bolsitas_per_ristra', parseInt(e.target.value))} />
                    </div>
                    <div style={{ marginBottom: 6 }}>
                        <div className={styles.inspectorLabel}>{t('scada.pesoBolsitaG', 'Peso bolsita (g)')}</div>
                        <input className={styles.inspectorInput} type="number"
                            value={config.peso_bolsita_g ?? 10} min={0.1} step={0.1}
                            onChange={e => setConfig('peso_bolsita_g', parseFloat(e.target.value))} />
                    </div>
                    <div style={{ marginBottom: 6 }}>
                        <div className={styles.inspectorLabel}>{t('scada.lanesEnvasadora', 'Pistas envasadora')}</div>
                        <input className={styles.inspectorInput} type="number"
                            value={config.lanes_envasadora ?? 6} min={2} max={20} step={2}
                            onChange={e => {
                                const v = parseInt(e.target.value);
                                setConfig('lanes_envasadora', v % 2 === 0 ? v : v + 1);
                            }} />
                        <div style={{ fontSize: 9, color: '#64748B', marginTop: 2 }}>
                            {t('scada.lanesEnvasadoraHint', 'Solo pistas pares (se dividen en 2 mitades para doblar)')}
                        </div>
                    </div>
                    {(() => {
                        const ristras = parseInt(config.ristras_per_carton ?? 6);
                        const bolsitas = parseInt(config.bolsitas_per_ristra ?? 5);
                        const pesoG = parseFloat(config.peso_bolsita_g ?? 10);
                        const totalBolsas = ristras * bolsitas;
                        const pesoCartonKg = (totalBolsas * pesoG / 1000).toFixed(2);
                        return (
                            <div style={{ fontSize: 9, color: '#94A3B8', background: '#0F172A', borderRadius: 4, padding: 6 }}>
                                {t('scada.cartonSummary', 'Resumen carton')}: <strong style={{ color: '#F59E0B' }}>{totalBolsas}</strong> {t('scada.bolsitas', 'bolsitas')} = <strong style={{ color: '#F59E0B' }}>{pesoCartonKg} kg</strong>
                            </div>
                        );
                    })()}
                </div>
            )}

            {/* Pump: inlet / outlet count */}
            {node.type === 'pump' && (
                <div className={styles.inspectorSection}>
                    <div className={styles.inspectorLabel}>{t('scada.inletsLeft')}</div>
                    <select className={styles.inspectorSelect} value={config.inlet_count ?? 1} onChange={e => setConfig('inlet_count', parseInt(e.target.value))}>
                        {[1,2,3,4].map(n => <option key={n} value={n}>{inletLabel(n)}</option>)}
                    </select>
                    <div className={styles.inspectorLabel} style={{ marginTop: 8 }}>{t('scada.outletsRight')}</div>
                    <select className={styles.inspectorSelect} value={config.outlet_count ?? 1} onChange={e => setConfig('outlet_count', parseInt(e.target.value))}>
                        {[1,2,3,4].map(n => <option key={n} value={n}>{outletLabel(n)}</option>)}
                    </select>
                </div>
            )}

            {/* Bucket elevator: inlet / outlet count + outlet gate sensors */}
            {node.type === 'bucket_elevator' && (
                <div className={styles.inspectorSection}>
                    <div className={styles.inspectorLabel}>{t('scada.inletsTop')}</div>
                    <select className={styles.inspectorSelect} value={config.inlet_count ?? 1} onChange={e => setConfig('inlet_count', parseInt(e.target.value))}>
                        <option value={1}>{inletLabel(1)}</option>
                        <option value={2}>{inletLabel(2)}</option>
                    </select>
                    <div className={styles.inspectorLabel} style={{ marginTop: 8 }}>{t('scada.outletsBottom')}</div>
                    <select className={styles.inspectorSelect} value={config.outlet_count ?? 1} onChange={e => setConfig('outlet_count', parseInt(e.target.value))}>
                        {[1,2,3,4].map(n => <option key={n} value={n}>{outletLabel(n)}</option>)}
                    </select>
                    <div className={styles.inspectorLabel} style={{ marginTop: 8 }}>{t('scada.gateSensorType')}</div>
                    <select className={styles.inspectorSelect} value={config.outlet_sensor_type || 'single'} onChange={e => setConfig('outlet_sensor_type', e.target.value)}>
                        <option value="single">{t('scada.sensorSingle')}</option>
                        <option value="dual">{t('scada.sensorDual')}</option>
                    </select>
                    {(config.outlet_sensor_type || 'single') === 'single' && (
                        <>
                            <div className={styles.inspectorLabel} style={{ marginTop: 6 }}>{t('scada.openLogic')}</div>
                            <select className={styles.inspectorSelect} value={config.outlet_open_level || 'high'} onChange={e => setConfig('outlet_open_level', e.target.value)}>
                                <option value="high">{t('scada.signalHighOpen')}</option>
                                <option value="low">{t('scada.signalLowOpen')}</option>
                            </select>
                        </>
                    )}
                    {Array.from({ length: config.outlet_count ?? 1 }, (_, i) => {
                        const key      = `outlet_${i + 1}`;
                        const closeKey = `outlet_${i + 1}_close`;
                        return (
                            <div key={key} style={{ marginTop: 8 }}>
                                <SignalPicker label={t('scada.outletNopen', { n: i + 1 })} dataType="boolean"
                                    bound={config.variables?.[key]} devices={devices}
                                    onChange={b => setSignalBinding(key, b)} />
                                {(config.outlet_sensor_type || 'single') === 'dual' && (
                                    <SignalPicker label={t('scada.outletNclose', { n: i + 1 })} dataType="boolean"
                                        bound={config.variables?.[closeKey]} devices={devices}
                                        onChange={b => setSignalBinding(closeKey, b)} />
                                )}
                            </div>
                        );
                    })}
                    <div className={styles.inspectorLabel} style={{ marginTop: 8 }}>{t('scada.colorOpen')}</div>
                    <input type="color" value={config.on_color || '#22C55E'} onChange={e => setConfig('on_color', e.target.value)} className={styles.colorSwatch} />
                    <div className={styles.inspectorLabel} style={{ marginTop: 6 }}>{t('scada.colorClosed')}</div>
                    <input type="color" value={config.off_color || '#EF4444'} onChange={e => setConfig('off_color', e.target.value)} className={styles.colorSwatch} />
                </div>
            )}

            {/* Ribbon blender: gate count + sensor type */}
            {node.type === 'ribbon_blender' && (
                <div className={styles.inspectorSection}>
                    <div className={styles.inspectorLabel}>{t('scada.gateCount')}</div>
                    <select className={styles.inspectorSelect} value={config.gate_count ?? 0} onChange={e => setConfig('gate_count', parseInt(e.target.value))}>
                        <option value={0}>{t('scada.noGates')}</option>
                        <option value={1}>{gateLabel(1)}</option>
                        <option value={2}>{gateLabel(2)}</option>
                    </select>
                    {(config.gate_count ?? 0) > 0 && (
                        <div style={{ marginTop: 8 }}>
                            <div className={styles.inspectorLabel}>{t('scada.gateSensorType')}</div>
                            <select className={styles.inspectorSelect} value={config.gate_sensor_type || 'single'} onChange={e => setConfig('gate_sensor_type', e.target.value)}>
                                <option value="single">{t('scada.sensorSingle')}</option>
                                <option value="dual">{t('scada.sensorDual')}</option>
                            </select>
                            {(config.gate_sensor_type || 'single') === 'single' && (
                                <>
                                    <div className={styles.inspectorLabel} style={{ marginTop: 6 }}>{t('scada.openLogic')}</div>
                                    <select className={styles.inspectorSelect} value={config.gate_open_level || 'high'} onChange={e => setConfig('gate_open_level', e.target.value)}>
                                        <option value="high">{t('scada.signalHighOpen')}</option>
                                        <option value="low">{t('scada.signalLowOpen')}</option>
                                    </select>
                                </>
                            )}
                            <div style={{ marginTop: 6 }}>
                                <SignalPicker label={t('scada.gateNopen', { n: 1 })} dataType="boolean"
                                    bound={config.variables?.gate_1} devices={devices}
                                    onChange={b => setSignalBinding('gate_1', b)} />
                                {(config.gate_count ?? 0) >= 2 && (
                                    <SignalPicker label={t('scada.gateNopen', { n: 2 })} dataType="boolean"
                                        bound={config.variables?.gate_2} devices={devices}
                                        onChange={b => setSignalBinding('gate_2', b)} />
                                )}
                            </div>
                            {(config.gate_sensor_type || 'single') === 'dual' && (
                                <div style={{ marginTop: 6 }}>
                                    <SignalPicker label={t('scada.gateNclose', { n: 1 })} dataType="boolean"
                                        bound={config.variables?.gate_1_close} devices={devices}
                                        onChange={b => setSignalBinding('gate_1_close', b)} />
                                    {(config.gate_count ?? 0) >= 2 && (
                                        <SignalPicker label={t('scada.gateNclose', { n: 2 })} dataType="boolean"
                                            bound={config.variables?.gate_2_close} devices={devices}
                                            onChange={b => setSignalBinding('gate_2_close', b)} />
                                    )}
                                </div>
                            )}
                            <div className={styles.inspectorLabel} style={{ marginTop: 8 }}>{t('scada.colorOpen')}</div>
                            <input type="color" value={config.on_color || '#22C55E'} onChange={e => setConfig('on_color', e.target.value)} className={styles.colorSwatch} />
                            <div className={styles.inspectorLabel} style={{ marginTop: 6 }}>{t('scada.colorClosed')}</div>
                            <input type="color" value={config.off_color || '#EF4444'} onChange={e => setConfig('off_color', e.target.value)} className={styles.colorSwatch} />
                        </div>
                    )}
                </div>
            )}

            {/* Cooler blender: outlet gate sensor */}
            {node.type === 'cooler_blender' && (
                <div className={styles.inspectorSection}>
                    <div className={styles.inspectorLabel}>{t('scada.outletGateSensor')}</div>
                    <select className={styles.inspectorSelect} value={config.outlet_sensor_type || 'single'} onChange={e => setConfig('outlet_sensor_type', e.target.value)}>
                        <option value="single">{t('scada.sensorSingle')}</option>
                        <option value="dual">{t('scada.sensorDual')}</option>
                    </select>
                    {(config.outlet_sensor_type || 'single') === 'single' && (
                        <>
                            <div className={styles.inspectorLabel} style={{ marginTop: 6 }}>{t('scada.openLogic')}</div>
                            <select className={styles.inspectorSelect} value={config.outlet_open_level || 'high'} onChange={e => setConfig('outlet_open_level', e.target.value)}>
                                <option value="high">{t('scada.signalHighOpen')}</option>
                                <option value="low">{t('scada.signalLowOpen')}</option>
                            </select>
                        </>
                    )}
                    <div style={{ marginTop: 6 }}>
                        <SignalPicker
                            label={(config.outlet_sensor_type || 'single') === 'dual' ? t('scada.outletOpenSensor') : t('scada.outletSignal')}
                            dataType="boolean"
                            bound={config.variables?.outlet_state} devices={devices}
                            onChange={b => setSignalBinding('outlet_state', b)} />
                    </div>
                    {(config.outlet_sensor_type || 'single') === 'dual' && (
                        <div style={{ marginTop: 6 }}>
                            <SignalPicker label={t('scada.outletClosedSensor')} dataType="boolean"
                                bound={config.variables?.outlet_state_close} devices={devices}
                                onChange={b => setSignalBinding('outlet_state_close', b)} />
                        </div>
                    )}
                    <div className={styles.inspectorLabel} style={{ marginTop: 8 }}>{t('scada.colorOpen')}</div>
                    <input type="color" value={config.on_color || '#22C55E'} onChange={e => setConfig('on_color', e.target.value)} className={styles.colorSwatch} />
                    <div className={styles.inspectorLabel} style={{ marginTop: 6 }}>{t('scada.colorClosed')}</div>
                    <input type="color" value={config.off_color || '#EF4444'} onChange={e => setConfig('off_color', e.target.value)} className={styles.colorSwatch} />
                </div>
            )}

            {/* Discrete capacitive level sensors (tank, silo, hopper_screw) */}
            {['tank', 'silo', 'hopper_screw'].includes(node.type) && (
                <div className={styles.inspectorSection}>
                    <div className={styles.inspectorLabel}>{t('scada.capSensors')}</div>
                    <select className={styles.inspectorSelect}
                        value={config.level_cap_count ?? 0}
                        onChange={e => setConfig('level_cap_count', parseInt(e.target.value))}>
                        <option value={0}>{t('scada.noCapSensors')}</option>
                        {[1,2,3,4,5,6].map(n => <option key={n} value={n}>{sensorLabel(n)}</option>)}
                    </select>
                    {Array.from({ length: config.level_cap_count ?? 0 }, (_, i) => {
                        const key = `level_cap_${i + 1}`;
                        return (
                            <div key={key} style={{ marginTop: 8 }}>
                                <SignalPicker
                                    label={t('scada.capSensorLabel', { n: i + 1 })}
                                    dataType="boolean"
                                    bound={config.variables?.[key]}
                                    devices={devices}
                                    onChange={binding => setSignalBinding(key, binding)}
                                />
                            </div>
                        );
                    })}
                </div>
            )}

            {node.type === 'display_trend' && (
                <div className={styles.inspectorSection}>
                    <div className={styles.inspectorLabel}>{t('scada.lineColor')}</div>
                    <input type="color" value={config.line_color || '#4F46E5'} onChange={e => setConfig('line_color', e.target.value)} className={styles.colorSwatch} />
                    <div className={styles.inspectorLabel} style={{ marginTop: 6 }}>{t('scada.maxPoints')}</div>
                    <input className={styles.inspectorInput} type="number" value={config.max_points || 60} onChange={e => setConfig('max_points', parseInt(e.target.value))} min={10} max={300} />
                </div>
            )}

            {/* Simulation config section */}
            <SimulationConfigSection
                node={node}
                allNodes={allNodes}
                config={config}
                onChange={onChange}
            />

            <button className={styles.deleteBtn} onClick={() => onDelete(node.id)}>
                <FontAwesomeIcon icon={faTrash} className="me-2" />
                {t('scada.deleteElement')}
            </button>
        </div>
    );
}
